import { useSelector } from 'react-redux';

import { isScrollingDisabled } from '../ducks/ui.duck';
import { getMarketplaceEntities } from '../ducks/marketplaceData.duck';
import { hasPermissionToViewData, isUserAuthorized } from '../util/userHelpers';

type ProfilePageSlice = {
  userId?: { uuid: string } | null;
  userShowError?: any;
  queryListingsError?: any;
  userListingRefs?: Array<{ type: 'listing'; id: { uuid: string } }>;
  reviews?: any[];
  queryReviewsError?: any;
};

type ReduxState = {
  user: { currentUser: any };
  ProfilePage: ProfilePageSlice;
};

export type CustomProfilePageState = {
  scrollingDisabled: boolean;
  currentUser: any;
  user: any;
  useCurrentUser: boolean;
  userShowError: any;
  queryListingsError: any;
  listings: any[];
  reviews: any[];
  queryReviewsError: any;
  isCurrentUser: boolean;
};

/**
 * Selector hook for CustomProfilePage. Reads from the existing
 * `ProfilePage.duck` slice so we don't have to duplicate `loadData` wiring,
 * and exposes a single typed view-model to the page.
 *
 * @param pathParamsId - The :id from the URL (Sharetribe user UUID string)
 */
export const useCustomProfilePage = (pathParamsId?: string): CustomProfilePageState =>
  useSelector((state: ReduxState) => {
    const currentUser = state.user?.currentUser;
    const {
      userId,
      userShowError,
      queryListingsError,
      userListingRefs = [],
      reviews = [],
      queryReviewsError,
    } = state.ProfilePage || {};

    const userMatches = getMarketplaceEntities(state, [{ type: 'user', id: userId }]);
    const user = userMatches.length === 1 ? userMatches[0] : null;

    const isCurrentUser =
      !!currentUser?.id?.uuid && currentUser?.id?.uuid === pathParamsId;
    const useCurrentUser =
      isCurrentUser &&
      !(isUserAuthorized(currentUser) && hasPermissionToViewData(currentUser));

    return {
      scrollingDisabled: isScrollingDisabled(state as any),
      currentUser,
      user,
      useCurrentUser,
      userShowError,
      queryListingsError,
      listings: getMarketplaceEntities(state, userListingRefs),
      reviews,
      queryReviewsError,
      isCurrentUser,
    };
  });
